import React, { useState } from 'react';
import PostalCodeLookup from '../UI/PostalCodeLookup';
import toast from '../../utils/toast';

/**
 * Example Work Site Form with Postal Code Lookup Integration
 * 
 * This demonstrates how to use the PostalCodeLookup component in forms
 */
const WorkSiteForm = ({ projectCode, onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    project: projectCode || '',
    site_name: '',
    site_description: '',
    site_address: '',
    site_postal_code: '',
    unit_number: '',
    site_latitude: '',
    site_longitude: '',
    site_geofence_radius: 150,
    site_type: 'main',
    site_status: 'active',
    site_requires_checkin: true,
    site_is_primary: false,
    site_access_instructions: '',
    site_safety_requirements: '',
    ...initialData
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [radiusManuallyEdited, setRadiusManuallyEdited] = useState(!!initialData);

  // Suggested geofence radius per site type (meters)
  const suggestedRadiusByType = {
    main: 200,
    office: 100,
    workshop: 200,
    storage: 150,
    parking: 150,
    equipment_yard: 200,
    foundation: 200,
    structure: 200,
    mep: 150,
    finishing: 150,
    landscaping: 200,
    other: 150,
  };

  // When site type changes, suggest a radius (only if not manually edited)
  const handleSiteTypeChange = (newType) => {
    const updates = { site_type: newType };
    if (!radiusManuallyEdited && suggestedRadiusByType[newType]) {
      updates.site_geofence_radius = suggestedRadiusByType[newType];
    }
    setFormData({ ...formData, ...updates });
  };

  // Handle postal code lookup success
  const handleAddressFound = (geocodedData) => {
    setFormData({
      ...formData,
      site_address: geocodedData.address,
      site_postal_code: geocodedData.postal_code || '',
      site_latitude: geocodedData.latitude,
      site_longitude: geocodedData.longitude
    });

    // Clear any previous address-related errors
    setErrors({
      ...errors,
      site_address: '',
      site_latitude: '',
      site_longitude: ''
    });

    console.log('📍 Auto-filled address:', geocodedData);
  };

  // Handle postal code lookup error
  const handleLookupError = (error) => {
    console.error('Geocoding error:', error);
    // You might want to show this error in the form
  };

  // Handle manual coordinate input
  const handleCoordinateChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Basic validation
      const newErrors = {};
      
      if (!formData.site_name.trim()) {
        newErrors.site_name = 'Site name is required';
      }
      
      if (!formData.site_address.trim()) {
        newErrors.site_address = 'Site address is required';
      }

      // Validate coordinates if provided
      if (formData.site_latitude && formData.site_longitude) {
        const lat = parseFloat(formData.site_latitude);
        const lng = parseFloat(formData.site_longitude);
        
        if (isNaN(lat) || isNaN(lng)) {
          newErrors.coordinates = 'Invalid coordinate format';
        } else if (lat < 1.1 || lat > 1.5 || lng < 103.6 || lng > 104.1) {
          newErrors.coordinates = 'Coordinates should be within Singapore region';
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error('Please fix the form errors');
        return;
      }

      // Submit the form
      if (onSubmit) {
        await onSubmit(formData);
      }

      toast.success('✅ Work site saved successfully!');
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('❌ Failed to save work site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteTypes = [
    { value: 'main', label: 'Main Site' },
    { value: 'office', label: 'Office Area' },
    { value: 'workshop', label: 'Workshop/Factory' },
    { value: 'storage', label: 'Storage/Warehouse' },
    { value: 'parking', label: 'Parking Area' },
    { value: 'equipment_yard', label: 'Equipment Yard' },
    { value: 'foundation', label: 'Foundation Work' },
    { value: 'structure', label: 'Structural Work' },
    { value: 'mep', label: 'MEP (Mechanical/Electrical/Plumbing)' },
    { value: 'finishing', label: 'Finishing Work' },
    { value: 'landscaping', label: 'Landscaping' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-lg">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-base sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2 truncate">
          {initialData ? 'Edit Work Site' : 'Add New Work Site'}
        </h2>
        <p className="text-gray-600">
          Create a work site for project <span className="font-medium">{projectCode}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Name *
            </label>
            <input
              type="text"
              value={formData.site_name}
              onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
              placeholder="e.g., Main Building, Foundation Area"
              className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.site_name ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.site_name && (
              <p className="text-red-600 text-sm mt-1">{errors.site_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Type
            </label>
            <select
              value={formData.site_type}
              onChange={(e) => handleSiteTypeChange(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {siteTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.site_description}
            onChange={(e) => setFormData({ ...formData, site_description: e.target.value })}
            placeholder="Detailed description of this work site..."
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* 🌟 POSTAL CODE LOOKUP INTEGRATION */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📍 Location & Address</h3>
          
          {/* Enhanced Postal Code Lookup Component */}
          <PostalCodeLookup
            value={formData.site_postal_code}
            onAddressFound={handleAddressFound}
            onPostalCodeChange={(newPostalCode) => {
              setFormData({ ...formData, site_postal_code: newPostalCode });
            }}
            onError={handleLookupError}
            placeholder="Enter postal code to auto-fill address (e.g., 018989)"
            className="mb-6"
          />

          {/* Manual Address Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Address *
              </label>
              <textarea
                value={formData.site_address}
                onChange={(e) => setFormData({ ...formData, site_address: e.target.value })}
                placeholder="Full address of the work site (auto-filled from postal code lookup)"
                rows={2}
                className={`w-full px-4 py-2.5 border rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus:border-blue-500 ${
                  errors.site_address ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.site_address && (
                <p className="text-red-600 text-sm mt-1">{errors.site_address}</p>
              )}
            </div>



            {/* Unit Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit Number
              </label>
              <input
                type="text"
                value={formData.unit_number}
                onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                placeholder="e.g., 12-03, #01-02"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* GPS Coordinates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🌐 Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.site_latitude}
                  onChange={(e) => handleCoordinateChange('site_latitude', e.target.value)}
                  placeholder="1.284038 (auto-filled)"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🌐 Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.site_longitude}
                  onChange={(e) => handleCoordinateChange('site_longitude', e.target.value)}
                  placeholder="103.851287 (auto-filled)"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus-visible:ring-2 focus-visible:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {errors.coordinates && (
              <p className="text-red-600 text-sm">{errors.coordinates}</p>
            )}

            {/* Distance Calculator */}
            {formData.site_latitude && formData.site_longitude && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-800">
                  📍 <strong>Coordinates:</strong> {parseFloat(formData.site_latitude).toFixed(6)}, {parseFloat(formData.site_longitude).toFixed(6)}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Geofence radius: {formData.site_geofence_radius} meters
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Geofence Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Geofence Radius (meters)
            </label>
            <input
              type="number"
              min="50"
              max="500"
              value={formData.site_geofence_radius}
              onChange={(e) => {
                setRadiusManuallyEdited(true);
                setFormData({ ...formData, site_geofence_radius: parseInt(e.target.value) || 150 });
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Detection radius for automatic check-in (50-500 meters)
            </p>
            {suggestedRadiusByType[formData.site_type] && (
              <p className="text-xs text-blue-600 mt-1">
                Suggested for {formData.site_type.replace(/_/g, ' ')}: {suggestedRadiusByType[formData.site_type]}m
                {!radiusManuallyEdited && ' (auto-applied)'}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="requires_checkin"
                checked={formData.site_requires_checkin}
                onChange={(e) => setFormData({ ...formData, site_requires_checkin: e.target.checked })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500"
              />
              <label htmlFor="requires_checkin" className="ml-2 text-sm text-gray-700">
                Requires check-in
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_primary"
                checked={formData.site_is_primary}
                onChange={(e) => setFormData({ ...formData, site_is_primary: e.target.checked })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500"
              />
              <label htmlFor="is_primary" className="ml-2 text-sm text-gray-700">
                Primary site for project
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 font-medium"
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update Work Site' : 'Create Work Site'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WorkSiteForm; 